import { docsGuideHref } from "@/lib/routes";
import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/client";
import { Button, Card, PageHeading, control, controlStyle } from "@/ui";
import { FieldHelp } from "@/components/settings/FieldHelp";
import { BlogSearchAppearance } from "@/components/settings/BlogSearchAppearance";
import { GoodsenderDomainForm } from "@/components/domains/GoodsenderDomainForm";
import { useGoodsenderDomain } from "@/lib/workspaces";
import {
  blogAddressHost,
  postAddressReadiness,
  viewBlogHref,
} from "@/lib/blog/viewBlogLink";
import { addressReadinessReason } from "@/lib/composerDomainGate";
import { ACTIVE_BLOG_THEME } from "../../../../../functions/src/blog/theme";
import { SOCIAL_EXTRAS_MAX } from "../../../../../functions/src/blog/socialLinks";

const muted = { color: "var(--gs-muted)" };
const LEDE = "Search appearance, profile links and Google Analytics for your blog.";
const EXTRAS_INVALID = "Give every link both a label and an https:// URL, or remove it.";
const TITLE_REQUIRED = "Enter a blog title.";

type SocialDraft = {
  linkedin: string;
  github: string;
  x: string;
  youtube: string;
  instagram: string;
  email: string;
  website: string;
  extras: { label: string; url: string }[];
};

const EMPTY_SOCIAL: SocialDraft = {
  linkedin: "",
  github: "",
  x: "",
  youtube: "",
  instagram: "",
  email: "",
  website: "",
  extras: [],
};

type ConfigDraft = {
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  analyticsTagId: string;
  social: SocialDraft;
};

const EMPTY: ConfigDraft = {
  title: "",
  description: "",
  seoTitle: "",
  seoDescription: "",
  analyticsTagId: "",
  social: EMPTY_SOCIAL,
};

export const Route = createFileRoute("/dashboard/w/$workspaceId/configuration")({
  component: ConfigurationRoute,
});

function ConfigurationRoute() {
  const { workspaceId } = Route.useParams();
  return <Configuration key={workspaceId} workspaceId={workspaceId} />;
}

function Configuration({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const goodsenderDomain = useGoodsenderDomain(workspaceId);
  const blogOptions = trpc.authenticatedRouterFunction.getBlog.queryOptions({ workspaceId });
  const blogQuery = useQuery(blogOptions);
  const saved: ConfigDraft = blogQuery.data
    ? {
        title: blogQuery.data.titleIsCustom ? blogQuery.data.title : "",
        description: blogQuery.data.description,
        seoTitle: blogQuery.data.seoTitle ?? "",
        seoDescription: blogQuery.data.seoDescription ?? "",
        analyticsTagId: blogQuery.data.analyticsTagId ?? "",
        social: {
          linkedin: blogQuery.data.socialLinks?.linkedin ?? "",
          github: blogQuery.data.socialLinks?.github ?? "",
          x: blogQuery.data.socialLinks?.x ?? "",
          youtube: blogQuery.data.socialLinks?.youtube ?? "",
          instagram: blogQuery.data.socialLinks?.instagram ?? "",
          email: blogQuery.data.socialLinks?.email ?? "",
          website: blogQuery.data.socialLinks?.website ?? "",
          extras: blogQuery.data.socialLinks?.extras ?? [],
        },
      }
    : EMPTY;
  const [draft, setDraft] = useState<ConfigDraft | null>(null);
  const editRevision = useRef(0);
  const titleEdited = useRef(false);
  const [titleError, setTitleError] = useState("");
  const form = draft ?? saved;
  const edit = (patch: Partial<ConfigDraft>) => {
    editRevision.current += 1;
    setDraft((current) => ({ ...(current ?? saved), ...patch }));
  };
  const [extrasError, setExtrasError] = useState("");
  const editExtras = (extras: SocialDraft["extras"]) => {
    setExtrasError("");
    edit({ social: { ...form.social, extras } });
  };

  const saveMutation = useMutation({
    ...trpc.authenticatedRouterFunction.updateBlogSettings.mutationOptions(),
    onError: (err) => {
      toast.error("Failed to save your configuration", {
        description: err.message ?? "Unknown error occurred.",
      });
    },
  });

  const handleSave = () => {
    const title = form.title.trim();
    if (titleEdited.current && title === "") {
      setTitleError(TITLE_REQUIRED);
      return;
    }
    setTitleError("");
    const s = form.social;
    const trimmedExtras = s.extras.map((e) => ({ label: e.label.trim(), url: e.url.trim() }));
    const extrasInvalid = trimmedExtras.some((e) => {
      if (e.label === "" && e.url === "") return false;
      return e.label === "" || e.url === "" || !e.url.startsWith("https://");
    });
    if (extrasInvalid) {
      setExtrasError(EXTRAS_INVALID);
      return;
    }
    setExtrasError("");
    const extras = trimmedExtras.filter((e) => e.label !== "" || e.url !== "");
    const socialEntries = {
      ...(s.linkedin.trim() && { linkedin: s.linkedin.trim() }),
      ...(s.github.trim() && { github: s.github.trim() }),
      ...(s.x.trim() && { x: s.x.trim() }),
      ...(s.youtube.trim() && { youtube: s.youtube.trim() }),
      ...(s.instagram.trim() && { instagram: s.instagram.trim() }),
      ...(s.email.trim() && { email: s.email.trim() }),
      ...(s.website.trim() && { website: s.website.trim() }),
      ...(extras.length > 0 && { extras }),
    };
    const savedRevision = editRevision.current;
    saveMutation.mutate(
      {
        workspaceId,
        ...(title ? { title } : {}),
        description: form.description,
        seoTitle: form.seoTitle,
        seoDescription: form.seoDescription,
        analyticsTagId: form.analyticsTagId.trim(),
        socialLinks: Object.keys(socialEntries).length > 0 ? socialEntries : null,
      },
      {
        onSuccess: (updatedBlog) => {
          toast.success("Configuration saved");
          queryClient.setQueryData(blogOptions.queryKey, (current) =>
            current ? { ...current, ...updatedBlog } : current,
          );
          if (editRevision.current === savedRevision) {
            titleEdited.current = false;
            setDraft(null);
          }
          void queryClient.invalidateQueries({ queryKey: blogOptions.queryKey });
        },
      },
    );
  };

  if (blogQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm" style={muted}>
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!blogQuery.data) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeading title="Configuration" lede={LEDE} />
        <p className="text-sm" style={muted}>
          Couldn&rsquo;t load your blog. Try again in a moment.
        </p>
      </div>
    );
  }

  const homeSignals = {
    goodsenderDomain,
    customDomain: blogQuery.data.customDomain,
    customDomainStatus: blogQuery.data.customDomainStatus,
    blogPaused: blogQuery.data.blogPaused,
    sendingDomain: blogQuery.data.sendingDomain,
  };
  const liveUrl = viewBlogHref(homeSignals);
  const addressHost = blogAddressHost(homeSignals);
  const notReadyReason = addressHost
    ? addressReadinessReason(postAddressReadiness(homeSignals), addressHost)
    : "Connect a domain to make this blog public.";

  return (
    <div className="flex flex-col gap-6">
      <PageHeading title="Configuration" lede={LEDE} />
      <Card title="Blog">
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Title
            <input
              aria-describedby={titleError ? "blog-title-error" : undefined}
              aria-invalid={titleError ? true : undefined}
              className={control}
              style={controlStyle}
              maxLength={255}
              value={form.title}
              onChange={(event) => {
                titleEdited.current = true;
                setTitleError("");
                edit({ title: event.target.value });
              }}
            />
            {titleError && (
              <span
                id="blog-title-error"
                role="alert"
                className="text-xs"
                style={{ color: "var(--gs-danger)" }}
              >
                {titleError}
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Description
            <textarea
              className={`${control} h-auto min-h-24 resize-y py-2`}
              style={controlStyle}
              value={form.description}
              onChange={(event) => edit({ description: event.target.value })}
            />
          </label>
          <GoodsenderDomainForm workspaceId={workspaceId} presentation="settings" />
        </div>
      </Card>
      <BlogSearchAppearance
        title={saved.title}
        description={saved.description}
        seoTitle={form.seoTitle}
        seoDescription={form.seoDescription}
        url={liveUrl ?? (addressHost ? `https://${addressHost}/` : "Public URL not configured")}
        urlStatus={liveUrl ? "Live" : `Not live — ${notReadyReason}`}
        onSeoTitleChange={(seoTitle) => edit({ seoTitle })}
        onSeoDescriptionChange={(seoDescription) => edit({ seoDescription })}
      />
      <Card title="Google Analytics">
        <div className="flex flex-col gap-4">
          <p className="text-xs" style={muted}>
            Report your blog&rsquo;s traffic into your own Google Analytics property, alongside
            the numbers GoodSender shows you on Analytics. Every public page is tagged, on your
            custom domain and your GoodSender address, and only once your blog is live.
          </p>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="blog-analytics-tag" className="text-sm font-medium">
              Google Analytics or Tag Manager ID
            </label>
            <input
              id="blog-analytics-tag"
              className={control}
              style={controlStyle}
              value={form.analyticsTagId}
              onChange={(e) => edit({ analyticsTagId: e.target.value })}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="G-XXXXXXXXXX"
            />
            <p className="text-xs" style={muted}>
              Paste a Google Analytics measurement ID (<code>G-XXXXXXXXXX</code>) or a Google Tag
              Manager container ID (<code>GTM-XXXXXXX</code>). Leave it empty for no analytics.
            </p>
            <p className="text-xs" style={muted}>
              The tag runs as Google documents it. Cookie consent, Consent Mode and any regional
              rules for your readers are yours to configure.
            </p>
            <p className="text-xs" style={muted}>
              <a
                href={docsGuideHref("analytics")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                How to connect Google Analytics
                <span className="sr-only"> (opens in a new tab)</span>
              </a>{" "}
              covers a standalone blog and a blog on a subdomain of your main site.
            </p>
          </div>
        </div>
      </Card>
      <Card title="Profile links">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-1.5">
            <p className="text-xs" style={muted}>
              Optional ways for readers to find and reach you. Shown publicly on your blog.
            </p>
            <FieldHelp label="Profile links" field="socialLinks" theme={ACTIVE_BLOG_THEME} />
          </div>
          {(
            [
              ["linkedin", "LinkedIn", "Profile URL or handle"],
              ["github", "GitHub", "Profile URL or username"],
              ["x", "X", "Profile URL or handle"],
              ["youtube", "YouTube", "Channel URL or handle"],
              ["instagram", "Instagram", "Profile URL or username"],
              ["email", "Email", "you@example.com"],
              ["website", "Website", "https://…"],
            ] as const
          ).map(([key, label, hint]) => (
            <div key={key} className="flex flex-col gap-1.5">
              <label htmlFor={`social-${key}`} className="text-sm font-medium">
                {label}
              </label>
              <input
                id={`social-${key}`}
                className={control}
                style={controlStyle}
                value={form.social[key]}
                onChange={(e) => edit({ social: { ...form.social, [key]: e.target.value } })}
                placeholder={hint}
              />
              {key === "email" && (
                <p className="text-xs" style={muted}>
                  Shown publicly as a contact link.
                </p>
              )}
            </div>
          ))}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Other links</p>
              <span className="text-xs tabular-nums" style={muted}>
                {form.social.extras.length} / {SOCIAL_EXTRAS_MAX}
              </span>
            </div>
            <p className="text-xs" style={muted}>
              Other profiles &mdash; Bluesky, Mastodon, a booking page&hellip;
            </p>
            {form.social.extras.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`Link ${i + 1} label`}
                  className={control}
                  style={controlStyle}
                  value={row.label}
                  onChange={(e) =>
                    editExtras(
                      form.social.extras.map((r, j) =>
                        j === i ? { ...r, label: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder="Label"
                />
                <input
                  aria-label={`Link ${i + 1} URL`}
                  className={control}
                  style={controlStyle}
                  value={row.url}
                  onChange={(e) =>
                    editExtras(
                      form.social.extras.map((r, j) => (j === i ? { ...r, url: e.target.value } : r)),
                    )
                  }
                  placeholder="https://…"
                />
                <Button
                  type="button"
                  variant="ghost"
                  iconOnly
                  aria-label={`Remove link ${i + 1}`}
                  onClick={() => editExtras(form.social.extras.filter((_, j) => j !== i))}
                >
                  &times;
                </Button>
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                disabled={form.social.extras.length >= SOCIAL_EXTRAS_MAX}
                onClick={() => editExtras([...form.social.extras, { label: "", url: "" }])}
              >
                Add link
              </Button>
            </div>
            {extrasError && (
              <p id="social-extras-error" role="alert" className="text-xs" style={{ color: "var(--gs-danger)" }}>
                {extrasError}
              </p>
            )}
          </div>
        </div>
      </Card>
      <div>
        <Button variant="primary" disabled={saveMutation.isPending} onClick={handleSave}>
          {saveMutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
          Save
        </Button>
      </div>
    </div>
  );
}
